import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Plus, Minus, ShoppingCart, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { API, useAuth } from '../App';
import { convertGoogleDriveLink } from '../utils/imageUtils';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [product, setProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const touchStartXRef = useRef(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${id}`);
        setProduct(response.data);
        setSelectedImage(convertGoogleDriveLink(response.data.main_image));
        if (response.data.variants && response.data.variants.length > 0) {
          setSelectedVariant(response.data.variants[0]);
        }
      } catch (error) {
        console.error('Failed to fetch product', error);
        toast.error('Failed to load product');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  // Keyboard arrow navigation for the image slider
  useEffect(() => {
    if (!product) return;
    const allImages = [product.main_image, ...(product.images || [])].filter(Boolean);
    if (allImages.length < 2) return;
    const handleKey = (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const tag = (e.target?.tagName || '').toLowerCase();
      if (['input', 'textarea', 'select'].includes(tag)) return;
      const idx = Math.max(0, allImages.findIndex(img => convertGoogleDriveLink(img) === selectedImage));
      const nextIdx = e.key === 'ArrowRight'
        ? (idx + 1) % allImages.length
        : (idx - 1 + allImages.length) % allImages.length;
      setSelectedImage(convertGoogleDriveLink(allImages[nextIdx]));
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [product, selectedImage]);

  const handleAddToCart = async () => {
    if (!user) {
      toast.error('Please login to add items to cart');
      navigate('/auth');
      return;
    }

    if (!selectedVariant) {
      toast.error('Please select a variant');
      return;
    }

    try {
      // Get current cart
      const cartResponse = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const currentCart = cartResponse.data.items || [];

      // Check if item already exists
      const existingItemIndex = currentCart.findIndex(
        item => item.product_id === product.id && item.variant_sku === selectedVariant.sku
      );

      let updatedCart;
      if (existingItemIndex >= 0) {
        // Update quantity
        updatedCart = [...currentCart];
        updatedCart[existingItemIndex].quantity += quantity;
      } else {
        // Add new item
        const newItem = {
          product_id: product.id,
          product_name: product.name,
          variant_sku: selectedVariant.sku,
          quantity: quantity,
          price: selectedVariant.price,
          image: product.main_image
        };
        updatedCart = [...currentCart, newItem];
      }

      await axios.post(`${API}/cart`, updatedCart, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Added to cart successfully!');
    } catch (error) {
      console.error('Failed to add to cart', error);
      toast.error('Failed to add to cart');
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!product) {
    return <div className="min-h-screen flex items-center justify-center">Product not found</div>;
  }

  return (
    <div className="min-h-screen py-12" data-testid="product-detail-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Images */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            {(() => {
              const allImages = [product.main_image, ...(product.images || [])].filter(Boolean);
              const currentIdx = Math.max(0, allImages.findIndex(img => convertGoogleDriveLink(img) === selectedImage));
              const goPrev = () => {
                if (allImages.length < 2) return;
                const prev = (currentIdx - 1 + allImages.length) % allImages.length;
                setSelectedImage(convertGoogleDriveLink(allImages[prev]));
              };
              const goNext = () => {
                if (allImages.length < 2) return;
                const next = (currentIdx + 1) % allImages.length;
                setSelectedImage(convertGoogleDriveLink(allImages[next]));
              };
              return (
                <>
                  <div
                    className="relative aspect-[3/4] overflow-hidden bg-accent mb-4 group select-none"
                    data-testid="product-image-slider"
                    onTouchStart={(e) => { touchStartXRef.current = e.touches[0].clientX; }}
                    onTouchEnd={(e) => {
                      const start = touchStartXRef.current;
                      if (start == null) return;
                      const dx = e.changedTouches[0].clientX - start;
                      touchStartXRef.current = null;
                      if (Math.abs(dx) < 40) return;
                      if (dx > 0) goPrev(); else goNext();
                    }}
                  >
                    <img
                      src={convertGoogleDriveLink(selectedImage)}
                      alt={product.name}
                      className="w-full h-full transition-transform duration-300"
                      style={{
                        objectFit: product.image_fit || 'cover',
                        objectPosition: `center ${product.image_position || 'center'}`,
                        transform: `scale(${Math.min(2, Math.max(0.5, product.image_zoom || 1))})`,
                      }}
                      data-testid="product-main-image"
                      draggable={false}
                    />

                    {allImages.length > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={goPrev}
                          aria-label="Previous image"
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/85 hover:bg-white text-foreground shadow-md backdrop-blur-sm transition-all opacity-0 group-hover:opacity-100 hover:scale-105 active:scale-95"
                          data-testid="product-image-prev"
                        >
                          <ChevronLeft size={22} strokeWidth={2} />
                        </button>
                        <button
                          type="button"
                          onClick={goNext}
                          aria-label="Next image"
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/85 hover:bg-white text-foreground shadow-md backdrop-blur-sm transition-all opacity-0 group-hover:opacity-100 hover:scale-105 active:scale-95"
                          data-testid="product-image-next"
                        >
                          <ChevronRight size={22} strokeWidth={2} />
                        </button>

                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                          {allImages.map((_, i) => (
                            <span
                              key={i}
                              className={`h-1.5 rounded-full transition-all ${i === currentIdx ? 'w-6 bg-primary' : 'w-1.5 bg-white/70'}`}
                            />
                          ))}
                        </div>

                        <div className="absolute top-3 right-3 bg-black/55 text-white text-xs font-medium px-2.5 py-1 rounded-full" data-testid="product-image-counter">
                          {currentIdx + 1} / {allImages.length}
                        </div>
                      </>
                    )}
                  </div>

                  {allImages.length > 1 && (
                    <div className="grid grid-cols-4 gap-4" data-testid="product-image-gallery">
                      {allImages.map((img, idx) => {
                        const converted = convertGoogleDriveLink(img);
                        const isActive = converted === selectedImage;
                        return (
                          <button
                            type="button"
                            key={idx}
                            className={`aspect-square overflow-hidden bg-accent cursor-pointer border-2 transition-colors ${
                              isActive ? 'border-primary' : 'border-transparent hover:border-primary/40'
                            }`}
                            onClick={() => setSelectedImage(converted)}
                            data-testid={`product-thumbnail-${idx}`}
                            aria-label={`View image ${idx + 1}`}
                          >
                            <img src={converted} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              );
            })()}
          </motion.div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-sm uppercase tracking-wide text-muted-foreground mb-2">{product.category}</p>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4" data-testid="product-name">{product.name}</h1>
            
            {user && user.status === 'approved' ? (
              <p className="text-3xl font-semibold text-luxury-accentGold mb-6" data-testid="product-price">
                &#8377;{selectedVariant ? selectedVariant.price.toFixed(2) : product.base_price.toFixed(2)}
              </p>
            ) : (
              <div className="mb-6 p-6 bg-luxury-gold/10 border-2 border-luxury-gold">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-12 h-12 bg-luxury-gold/20 flex items-center justify-center">
                    <svg className="w-6 h-6 text-luxury-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-xl text-luxury-gold">
                      {user && user.status === 'pending' ? 'Account Pending Approval' : 'Exclusive B2B Pricing'}
                    </h3>
                    <p className="text-muted-foreground">
                      {user && user.status === 'pending' 
                        ? 'Waiting for admin approval to view prices' 
                        : user && user.status === 'rejected'
                        ? 'Your account was not approved'
                        : 'Login to view wholesale prices'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/auth')}
                  className="w-full bg-luxury-gold hover:bg-luxury-darkGold text-white py-3 font-semibold transition-all duration-300"
                >
                  {user ? 'Contact Support' : 'Login to View Price & Purchase'}
                </button>
              </div>
            )}

            <div className="border-t border-b border-border py-6 mb-6">
              <p className="text-lg leading-relaxed text-muted-foreground">{product.description}</p>
            </div>

            <div className="space-y-6">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide mb-2">Fabric</p>
                <p className="text-muted-foreground">{product.fabric}</p>
              </div>
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide mb-2">Work Type</p>
                <p className="text-muted-foreground">{product.work_type}</p>
              </div>
            </div>

            {/* Variants */}
            {user && user.status === 'approved' && product.variants && product.variants.length > 0 && (
              <div className="mt-8" data-testid="variants-section">
                <p className="text-sm font-semibold uppercase tracking-wide mb-4">Select Variant</p>
                <div className="space-y-3">
                  {product.variants.map((variant) => (
                    <div
                      key={variant.sku}
                      onClick={() => setSelectedVariant(variant)}
                      className={`p-4 border-2 cursor-pointer transition-all duration-300 ${
                        selectedVariant?.sku === variant.sku
                          ? 'border-primary bg-accent'
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid={`variant-${variant.sku}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">
                            {variant.color} - {variant.size}
                          </p>
                          <p className="text-sm text-muted-foreground">Design No: {variant.sku}</p>
                          {/* Display Custom Fields */}
                          {variant.custom_fields && variant.custom_fields.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {variant.custom_fields.map((field, idx) => (
                                <p key={idx} className="text-sm text-muted-foreground">
                                  <span className="font-medium">{field.field_name}:</span> {field.field_value}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-luxury-accentGold">&#8377;{variant.price.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            {user && user.status === 'approved' && (
              <div className="mt-8">
                <p className="text-sm font-semibold uppercase tracking-wide mb-4">Quantity</p>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 border border-border hover:border-primary flex items-center justify-center transition-colors"
                    data-testid="decrease-quantity"
                  >
                    <Minus size={18} />
                  </button>
                  <span className="text-xl font-semibold w-12 text-center" data-testid="quantity-value">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 border border-border hover:border-primary flex items-center justify-center transition-colors"
                    data-testid="increase-quantity"
                  >
                    <Plus size={18} />
                  </button>
                </div>
              </div>
            )}

            {/* Add to Cart */}
            {user && user.status === 'approved' && (
              <button
                onClick={handleAddToCart}
                className="w-full mt-8 bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-4 font-medium text-lg transition-all duration-300 shadow-sm hover:shadow-md flex items-center justify-center space-x-2"
                data-testid="add-to-cart-button"
              >
                <ShoppingCart size={20} />
                <span>Add to Cart</span>
              </button>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
